import { collection, doc, setDoc, getDoc, updateDoc, onSnapshot, runTransaction } from 'firebase/firestore';
import { firestore, auth } from '../firebase';
import { supabase } from '../lib/supabase';
import { getDefaultOptions, getDefaultRetailData } from '../lib/getDefaultValues';
import { sanitizeQrForClient } from '../utils/sanitizeQrForClient';
import axios from 'axios';

// ----- AUTH TOKEN FUNCTION -----
const authToken = async () => {
    try {
		const user = auth.currentUser;
		if (!user) return null;
        return await user.getIdToken();
    } catch (error) {
        // Specific token error handling
        if (error.code === 'auth/network-request-failed') {
            throw new Error('Network error - please check your connection');
        }
        if (error.code === 'auth/user-token-expired') {
            throw new Error('Session expired - please log in again');
        }
        // Force logout on unrecoverable auth errors
        if (error.code === 'auth/invalid-user-token' || error.code === 'auth/user-disabled') {
            auth.signOut();
            throw new Error('Authentication invalid - logging out');
        }
        // Re-throw original error for other cases
        throw error;
    }
};
// ----- END AUTH TOKEN FUNCTION -----
// ----------------------------------------------

// ----- GET ROUTES -----
const getQR = async (qrId) => {
    const snapshot = await getDoc(doc(firestore, 'codes', qrId));
    if (snapshot.exists()) {
        return sanitizeQrForClient(snapshot.data());
    } else if (qrId.includes('-')) {
        return await getRetailObject(qrId);
    } else {
        return null;
    };
};

const getRetailObject = async (qrId) => {
    try {
        if (!qrId.includes("-")) return null;

        const snapshot = await getDoc(doc(firestore, 'retail', qrId));
        if (snapshot.exists()) {
            const data = snapshot.data();
            const defaultData = getDefaultRetailData(qrId, data.product_id);
            const qr_style = getDefaultOptions(qrId);

            if (data.product_id) {
                defaultData.product_id = data.product_id;
            }
            
            return {...defaultData, qr_style};
        } else return null;
    } catch (error) {
        console.error("Error retrieving retail object: ", error.message);
        return null;
    };
};

const getQRListener = (qrId, setQrObj) => {
    const qrRef = doc(firestore, 'codes', qrId);
    const qrListener = onSnapshot(qrRef, snapshot => {
        if (snapshot.exists()) {
            setQrObj(sanitizeQrForClient(snapshot.data()));
        } else {
            setQrObj({});
        };
    });

    return qrListener;
};

const getLogsListener = (type, qrId, setLogs) => {
    const logsRef = collection(firestore, 'codes', qrId, type);
    const logsListener = onSnapshot(logsRef, snap => {
        if (snap.size) {
            setLogs(snap.docs.map(log => log.data()));
        };
    });
    return logsListener;
};

const getSignature = async (qrId, date) => {
    const sigRef = doc(firestore, 'codes', qrId, 'logs', `${date}`);
    const docSnap = await getDoc(sigRef);

    if (docSnap.exists()) {
        return JSON.parse(docSnap.data().signature);
    } else {
        return null;
    };
};

const getGeneratorQr = async (qrId) => {
    const genQrSnap = await getDoc(doc(firestore, 'generator', qrId));

    if (genQrSnap.exists()) return genQrSnap.data();
    throw ('Does not exist');
};

const getSubQrData = async (data) => {
    return await axios.post(`/api/qr/subQr/get`, data, { 'Content-Type': 'application/json' });
};

const getProduct = async (pid) => {
    try {
        const productSnap = await getDoc(doc(firestore, "products", pid));
        if (!productSnap.exists()) {
          throw new Error("not-found");
        };

        return productSnap.data();
    } catch (error) {
        if (error.message !== "not-found") {
          console.error("Error getting product in Firestore call: ", error.message);
        }
        throw error;
    };
};
const getContinueUrl = async (hash) => {
    try {
        const { data, error } = await supabase
            .from('continue_urls')
            .select('cleaned_url')
            .eq('hash', hash)
            .single();

        if (error) {
            console.error('Supabase lookup error:', error);
            return null;
        }

        return data?.cleaned_url || null;
    } catch (err) {
        console.error('Error fetching continue URL:', err);
        return null;
    }
};

// ----- END GET ROUTES -----
// ----------------------------------------------

// ----- PUT ROUTES -----

const addUIDtoDB = async (data) => {
    return await authToken().then(token => {
        const config = {
            headers: { Authorization: `Bearer ${token}` }
        };

        return axios.put(`/api/users/new`, data, config);
    });
};

const updateUserObject = async (id, data) => {
    try {
        const token = await authToken();
        const config = {
            headers: { Authorization: `Bearer ${token}` }
        };

        return await axios.put(`/api/users/${id}`, { data }, config);
    } catch (err) {
        console.error('Error updating user object:', err);
        throw err;
    }
};

const createLiquidQR = async (randKey, data) => {
    return await authToken().then(token => {
        const config = {
            headers: { Authorization: `Bearer ${token}` }
        };

        return axios.put(`/api/qr/user/liquid/new/${randKey}`, data, config);
    });
};

const updateQR = async (id, data) => {
    return await authToken().then(token => {
        const config = {
            headers: { Authorization: `Bearer ${token}` }
        };

        return axios.put(`/api/qr/user/liquid/${id}`, data, config);
    });
};

const updateSonar = async (id, data) => {
    return await authToken().then(token => {
        const config = {
            headers: { Authorization: `Bearer ${token}` }
        };

        return axios.put(`/api/qr/sonar/${id}`, data, config);
    });
};

const linkQR = async (id, data) => {
    return await authToken().then(token => {
        const config = {
            headers: { Authorization: `Bearer ${token}` }
        };

        return axios.put(`/api/qr/user/liquid/link/${id}`, data, config);
    });
};

const createNewCategory = async (data) => {
    return await authToken().then(token => {
        const config = {
            headers: { Authorization: `Bearer ${token}` }
        };

        return axios.put(`/api/users/category`, data, config);
    });
};

const optimizeGif = async (data) => {
    try {
        const token = await authToken();
        const config = {
            headers: {
                Authorization: `Bearer ${token}`
            }
        };

        return await axios.put('/api/qr/user/fileUpload/gif', data, config);
    } catch (err) {
        console.error(err);
    };
};

const storeFiles = async (data) => {
    return await authToken().then(async token => {
        const config = {
            headers: {
                Authorization: `Bearer ${token}`
            }
        };

        return await axios.put(`/api/qr/user/fileUpload`, data, config);
    });
};

const storeSnapshot = async (id, data) => {
    return await authToken().then(async token => {
        const config = {
            headers: {
                Authorization: `Bearer ${token}`
            }
        };

        return axios.put(`/api/qr/user/snapUpload/${id}`, data, config);
    });
};

const storeConnectHeaderImage = async (id, data) => {
    return await authToken().then(async token => {
        const config = {
            headers: {
                Authorization: `Bearer ${token}`
            }
        };

        return axios.put(`/api/qr/user/connectHeader/${id}`, data, config);
    });
};

const deactivateQR = async (id) => {
    return await authToken().then(token => {
        const config = {
            headers: { Authorization: `Bearer ${token}` }
        };

        return axios.put(`/api/qr/delete/${id}`, {}, config);
    });
};

const resetPassword = async (email) => {
    return axios.put(`/api/admin/reset`, { email: email });
};

const addTableLog = async (qrId='', data={ date: 0 }, sigArr=[], type='') => {
    try {
        if (!qrId || !data?.date || !type) throw(new Error('missing data'));

        // Send log to backend
        await axios.post(`/api/qr/${qrId}/logs`, {
            data,
            sigArr,
            type
        });

        // Collect all file paths for fields that contain a file object with a path
        const files = [];
        for (const key in data) {
            if (data[key] && typeof data[key] === 'object' && data[key].path) {
                files.push({...data[key], key});
            }
        }

        // Only invoke server calls if at least one file exists
        if (files.length > 0) {
            await axios.post('/api/qr/log/processFileMove', {
                qrId: qrId,
                logId: data.date,
                qrDataType: type,
                files
            });
        }
        
        return true;
    } catch (err) {
        console.error(err);
        return err;
    };
};

const updateQRSchedule = async (id, data) => {
    return await authToken().then(token => {
        const config = {
            headers: { Authorization: `Bearer ${token}` }
        };

        return axios.put(`/api/qr/user/schedule/${id}`, data, config);
    });
};

const updateCategories = async (data) => {
    return await authToken().then(token => {
        const config = {
            headers: { Authorization: `Bearer ${token}` }
        };

        return axios.put(`/api/users/category/edit`, data, config);
    });
};

const updateTableLog = async (qrId = '', data = { date: 0 }, type = '') => {
    try {
        if (data.date === 0 || !qrId || !type) throw (new Error('missing data'));

        return await runTransaction(firestore, async (transaction) => {
            const logRef = doc(firestore, 'codes', qrId, type, `${data.date}`);
            const logSnap = await transaction.get(logRef);

            if (!logSnap.exists()) {
                return 'Document does not exist!';
            };

            let updatedLog;
            const log = logSnap.data();
            if (log.date === data.date) {
                const editedLog = {};
                for (const key in data) {
                    const logKeyVal = log[key] || '';
                    if (key !== 'edits' && key !== 'deleted' && key !== 'date_edited' && logKeyVal !== data[key]) {
                        if (Array.isArray(logKeyVal) && Array.isArray(data[key])) {
                            if (!logKeyVal.every(val => data[key].some(v => v === val)) || data[key].length !== logKeyVal.length) {
                                editedLog[key] = (log?.hasOwnProperty(key) && data[key]) ? (logKeyVal ?? '') : '';
                                log[key] = data[key];
                            } else continue;
                        } else {
                            editedLog[key] = (log?.hasOwnProperty(key) && data[key]) ? (logKeyVal ?? '') : '';
                            log[key] = data[key];
                        };
                    };
                };

                if (Object?.keys(editedLog)?.length < 1) {
                    return { ...log, date_edited: data.date_edited };
                };

                updatedLog = {
                    ...log,
                    date_edited: data.date_edited,
                    edits: log?.edits ? { ...log.edits, [log.date_edited]: { ...editedLog, date_active: log.date_edited, date_edited: data.date_edited } } : { [log.date]: { ...editedLog, date_active: log.date, date_edited: data.date_edited } }
                };
            };

            if (updatedLog?.hasOwnProperty('date_edited') && updatedLog.date_edited === data.date_edited) {
                transaction.update(logRef, updatedLog);
                return updatedLog;
            } else {
                return data;
            };
        });
    } catch (err) {
        console.error(err.message);
        return err;
    };
};

const unlinkQr = async (data) => {
    return await authToken().then(token => {
        const config = {
            headers: { Authorization: `Bearer ${token}` }
        };
        
        return axios.put(`/api/qr/unlink`, data, config);
    });
};

const updateLogToDeleted = async (id, date, type) => {
    try {
        if (!id || !date || !type) throw(new Error('missing data'));

        return await runTransaction(firestore, async (transaction) => {
            const logRef = doc(firestore, 'codes', id, type, `${date}`);
            transaction.update(logRef, { deleted: true });
            return true;
        });
    } catch (err) {
        console.error(err);
        return err;
    };
};

const addQrFromGenerator = async (data) => {
    return await authToken().then(token => {
        const config = {
            headers: { Authorization: `Bearer ${token}` }
        };

        return axios.put(`/api/users/qr/add`, data, config);
    });
};

const bitSignalAuth = async (obj) => {
    return await authToken().then(token => {
        const config = {
            headers: { Authorization: `Bearer ${token}` }
        };

        return axios.post(`/api/users/bsAuth`, obj, config);
    });
};

const initializeSubQr = async (data) => {
    return await axios.post('/api/qr/subQr/post', data);
};

const deleteFiles = async (fileData) => {
    return await authToken().then(async token => {
        const config = {
            headers: { Authorization: `Bearer ${token}` }
        };

        return axios.put(`/api/qr/delete/file`, fileData, config);
    });
};
// ----- END PUT ROUTES -----
// ----------------------------------------------

// ------- POST ROUTES ---------
const createWidget = async (widgetData) => {
    try {
        const { data, error } = await supabase
            .from('cta_widgets')
            .insert([widgetData])
            .select()
            .single()

        if (error) {
            return {
                error: error,
                ok: false
            }
        } else {
            return {
                ok: true,
                success: true,
                widgetId: data.id,
                updated_at: data.updated_at
            }
        }
    } catch (err) {
        console.error(err);
        throw new Error(err.message)
    }
}

const updateWidget = async (id, payload) => {
    try {
        const { data, error } = await supabase
            .from('cta_widgets')
            .update(payload)
            .eq('id', id)
            .select()
            .single();

        if (error) {
            return { ok: false, error };
        }
        return { ok: true, success: true, data };
    } catch (err) {
        console.error(err);
        return { ok: false, error: err };
    }
};

const sendOrgMemberInvite = async ({ inviteEmail, org_id, role = 'editor' }) => {
    if (!inviteEmail || !org_id) {
        throw new Error('Missing required parameters');
    }

    const token = await authToken();
    const config = {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    };

    return axios.post(`/api/org/invite`, { inviteEmail, org_id, role }, config);
};

const getInviteData = async (token) => {
    if (!token) {
        throw new Error('Invite token is required');
    }

    return axios.get(`/api/org/invite/${token}`);
};

const acceptOrgInvite = async (invite_token) => {
    if (!invite_token) {
        throw new Error('Invite token is required');
    }

    const token = await authToken();
    const config = {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    };

    return axios.post(`/api/org/invite/accept`, { invite_token }, config);
};

const invalidateOrgInvite = async ({ org_id, invite_token }) => {
    if (!org_id || !invite_token) {
        throw new Error('org_id and invite_token are required');
    }

    const token = await authToken();
    const config = {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    };

    return axios.patch(`/api/org/${org_id}/invite/${invite_token}/invalidate`, {}, config);
};

const updateUserRole = async ({ org_id, user_id, role }) => {
    if (!org_id || !user_id || !role) {
        throw new Error('org_id, user_id, and role are required');
    }

    const token = await authToken();
    const config = {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    };

    return axios.put(`/api/org/${org_id}/users/${user_id}/role`, { role }, config);
};

const removeUserFromOrg = async ({ org_id, user_id }) => {
    if (!org_id || !user_id) {
        throw new Error('org_id and user_id are required');
    }

    const token = await authToken();
    const config = {
        headers: {
            Authorization: `Bearer ${token}`
        }
    };

    return axios.delete(`/api/org/${org_id}/users/${user_id}`, config);
};

const reserveOrgId = async (data = {}) => {
    try {
        const token = await authToken();
        const config = {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        };

        return axios.post("/api/org/reserve", data, config);
    } catch (error) {
        console.error("Error sending reserve org API to server", error);
        throw error;
    };
};

const deleteReservedOrg = async (org_id) => {
    try {
        const token = await authToken();
        const config = {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        };

        return axios.delete(`/api/org/reserved/${org_id}`, config);
    } catch (error) {
        console.error("Error deleting reserved org:", error);
        throw error;
    }
};

const getOrgMetadata = async (org_id) => {
    try {
        const token = await authToken();

        const config = {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        };
        return axios.get(`/api/org/${org_id}/metadata`, config);
    } catch (error) {
        console.error("Error fetching org metadata:", error);
        throw error;
    }
};

const uploadOrgAvatar = async (org_id, formData) => {
    try {
        const token = await authToken();
        const config = {
            headers: {
                Authorization: `Bearer ${token}`
            }
        };

        return axios.put(`/api/org/${org_id}/avatar`, formData, config);
    } catch (error) {
        console.error('Error uploading organization avatar:', error);
        throw error;
    }
};

const removeOrgAvatar = async (org_id) => {
    try {
        const token = await authToken();
        const config = {
            headers: {
                Authorization: `Bearer ${token}`
            }
        };

        return axios.delete(`/api/org/${org_id}/avatar`, config);
    } catch (error) {
        console.error('Error removing organization avatar:', error);
        throw error;
    }
};

const updateOrg = async (org_id, data = {}) => {
    try {
        const token = await authToken();
        const config = {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
        };

        return axios.put(`/api/org/${org_id}`, data, config);
    } catch (error) {
        console.error("Error updating organization:", error);
        throw error;
    }
};
// ------ END POST ROUTES ---------
// --------------------------------

// ----- DELETE ROUTES -----
const deleteSnapshot = async (id) => {
    return await authToken().then(token => {
        const config = {
            headers: { Authorization: `Bearer ${token}` }
        };

        return axios.delete(`/api/qr/delete/snapDelete/${id}`, config);
    });
};
// ----- END DELETE ROUTES -----
// ----------------------------------------------

// ----- STRIPE ROUTES -----
const createStripeCustomerAPI = async (data) => {
    return await axios({
        method: 'post',
        url: `${process.env.REACT_APP_STRIPE_URL}/create`,
        data: data
    });
};

const createStripeCheckout = async (data) => {
    const token = await authToken();
    return await axios({
        method: 'post',
        url: `${process.env.REACT_APP_STRIPE_URL}/checkout`,
        data: data,
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    });
};

const createStripeCustomerSession = async (data) => {
    return await axios({
        method: 'post',
        url: `${process.env.REACT_APP_STRIPE_URL}/customer`,
        data
    })
};
// ----- END STRIPE ROUTES -----
// ----------------------------------------------

const API = {
    getQR,
    getQRListener,
    getLogsListener,
    getSignature,
    getGeneratorQr,
    getSubQrData,
    getProduct,
    getContinueUrl,
    initializeSubQr,
    addUIDtoDB,
    updateUserObject,
    createLiquidQR,
    updateQR,
    updateSonar,
    linkQR,
    createNewCategory,
    optimizeGif,
    storeFiles,
    storeSnapshot,
    storeConnectHeaderImage,
    deactivateQR,
    resetPassword,
    addTableLog,
    updateQRSchedule,
    updateLogToDeleted,
    createWidget,
    updateWidget,
    deleteFiles,
    deleteSnapshot,
    addQrFromGenerator,
    createStripeCustomerAPI,
    createStripeCheckout,
    // upgradeStripeSubscription,
    createStripeCustomerSession,
    updateCategories,
    updateTableLog,
    unlinkQr,
    reserveOrgId,
    deleteReservedOrg,
    getOrgMetadata,
    uploadOrgAvatar,
    removeOrgAvatar,
    updateOrg,
    sendOrgMemberInvite,
    getInviteData,
    acceptOrgInvite,
    invalidateOrgInvite,
    updateUserRole,
    removeUserFromOrg,
    bitSignalAuth
};

export default API;
